import { useCallback } from 'react';
import { useApiCall } from '@/hooks/useApiCall';
import { useApiClient } from '@/hooks/useApiClient';
import { influxListSignals, influxMachineStatus } from '@/features/signals/api/SignalsApiClient';
import type { InfluxMachineStatus, SignalTarget } from '@/features/signals/type/SignalsTypes';
import type { BaseResponse } from '@/lib/http/HttpClient';

type InfluxSignalsResponse = BaseResponse & { items: string[] };
type InfluxMachineStatusResponse = BaseResponse & InfluxMachineStatus;

/**
 * Signal-field discovery for the selected target — refetches whenever it changes. Callers
 * pass a memoised target: a new object on every render would refetch every render.
 */
export function useInfluxSignals(target: SignalTarget | null) {
  const http = useApiClient();

  const apiFn = useCallback(
    async (_signal: AbortSignal) => {
      const items = await influxListSignals(target!, http ?? undefined);
      return {
        ok: true as const,
        data: { timestamp: new Date().toISOString(), items },
      };
    },
    [target, http]
  );

  const { data, isLoading, error, retry } = useApiCall<InfluxSignalsResponse>({
    apiFn,
    immediate: target !== null,
    deps: [target],
  });

  return { data: data?.items ?? null, isLoading, error, retry };
}

/** "Dernière activité" status for the selected (memoised) target — refetches when it changes. */
export function useInfluxMachineStatus(target: SignalTarget | null) {
  const http = useApiClient();

  const apiFn = useCallback(
    async (_signal: AbortSignal) => {
      const status = await influxMachineStatus(target!, http ?? undefined);
      return {
        ok: true as const,
        data: { timestamp: new Date().toISOString(), ...status },
      };
    },
    [target, http]
  );

  const { data, isLoading, error, retry } = useApiCall<InfluxMachineStatusResponse>({
    apiFn,
    immediate: target !== null,
    deps: [target],
  });

  return { lastTime: data?.lastTime ?? null, isLoading, error, retry };
}
